import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  Router,
  Route,
  Switch,
  Redirect,
  Link,
  useLocation,
  useHistory,
} from "react-router-dom";
import { createBrowserHistory } from "history";
import {
  AppBar,
  Toolbar,
  Typography,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Palette } from "@mui/icons-material";
//---------------- Styles---------------- 
import './toggleSwitch.css';
import './App.css';
import './ButtonStyle.css';
import './loding.css';

//---------------- Auth---------------- 
import { AuthProvider, useAuth } from "./AuthContext";
import { ChatProvider } from "./ChatContext";
import { useChat } from "./ChatContext";

//---------------- Theme Context ----------------
import { ThemeProvider as CustomThemeProvider, useTheme as useCustomTheme } from "./ThemeContext";

//---------------- Navbar + Logout---------------- 
import HorizontalNavbar from "./HorizontalNavbar";
import MobileBottomNav from "./MobileBottomNav";
import Logout from "./Logout";

//---------------- PWA Components---------------- 
import PWAStatus from "./components/common/PWAStatus";

//---------------- PWA Registration (vite-plugin-pwa) ----------------
import { registerSW } from 'virtual:pwa-register';

//---------------- Loading Component ----------------
import LodingComponent from "./loding";

//-----------SessionTimeOut---------
import SessionTimeout from "./SessionTimeout";

// ============================================================================
// LAZY LOADED PAGE COMPONENTS (Route-based code splitting)
// Each page is loaded only when the user navigates to it
// ============================================================================

//-----------Company----------------
const CompanyEdit = lazy(() => import("./components/pages/Company/Edit"));

//-----------Home-------------------
const Home = lazy(() => import("./components/pages/Home"));

//-----------Role-------------------
const RoleIndex = lazy(() => import("./components/pages/Role/Index"));
const RoleDetail = lazy(() => import("./components/pages/Role/Detail"));

//-----------User-------------------
const UserIndex = lazy(() => import("./components/pages/User/Index"));
const UserCreate = lazy(() => import("./components/pages/User/Create"));
const UserEdit = lazy(() => import("./components/pages/User/Edit"));
const UserDetail = lazy(() => import("./components/pages/User/Detail"));
const PasswordChange = lazy(() => import("./components/pages/User/PasswordChange"));
const ThemeSettings = lazy(() => import("./components/pages/ThemeSettings"));

//-----------Product-------------------
const ProductCategoryIndex = lazy(() => import("./components/pages/ProductCategory/Index"));
const ProductItemIndex = lazy(() => import("./components/pages/ProductItem/Index"));
const ProductBrandIndex = lazy(() => import("./components/pages/ProductBrand/Index"));

//-----------Bank-------------------
const BankIndex = lazy(() => import("./components/pages/Bank/Index"));

//-----------Master-------------------
const MasterDashboard = lazy(() => import("./components/pages/Master/MasterDashboard"));
const ClientIndex = lazy(() => import("./components/pages/Master/ClientIndex"));
const VendorIndex = lazy(() => import("./components/pages/Master/VendorIndex"));

//-----------Messaging-------------------
const InternalMessaging = lazy(() => import("./components/pages/Messaging/InternalMessaging"));

//-----------AccountHead-------------------
const AccountHeadDashboard = lazy(() => import("./components/pages/AccountHead/AccountHeadDashboard"));
const AccountHeadIndex = lazy(() => import("./components/pages/AccountHead/AccountHeadIndex"));
const AssetIndex = lazy(() => import("./components/pages/AccountHead/AssetIndex"));
const CapitalIndex = lazy(() => import("./components/pages/AccountHead/CapitalIndex"));
const OtherOptionIndex = lazy(() => import("./components/pages/AccountHead/OtherOptionIndex"));
const SalesExecutiveIndex = lazy(() => import("./components/pages/AccountHead/SalesExecutiveIndex"));
const SalesManDashboard = lazy(() => import("./components/pages/SalesMan/SalesManDashboard"));
const SalesManSaleOrderIndex = lazy(() => import("./components/pages/SalesMan/SalesManSaleOrderIndex"));
const BalanceSheetHeadIndex = lazy(() => import("./components/pages/AccountHead/BalanceSheetHeadIndex"));

//-----------PurchaseOrder-------------------
const PurchaseOrderIndex = lazy(() => import("./components/pages/PurchaseOrder/PurchaseOrderIndex"));

//-----------Purchase-------------------
const PurchaseIndex = lazy(() => import("./components/pages/Purchase/PurchaseIndex"));

//-----------PurchaseReturn-------------------
const PurchaseReturnAgainstIndex = lazy(() => import("./components/pages/PurchaseReturn/PurchaseReturnAgainstIndex"));

//-----------Quotation-------------------
const QuotationIndex = lazy(() => import("./components/pages/Quotation/QuotationIndex"));

//-----------SalesOrder-------------------
const SalesOrderIndex = lazy(() => import("./components/pages/SaleOrder/SalesOrderIndex"));

//-----------Sale-------------------
const SaleIndex = lazy(() => import("./components/pages/Sale/SaleIndex"));

//-----------SaleReturn-------------------
const SaleReturnIndex = lazy(() => import("./components/pages/SaleReturn/SaleReturnIndex"));

//-----------Stock-------------------
const StockTransferInIndex = lazy(() => import("./components/pages/Stock/StockTransferInIndex"));
const StockTransferOutIndex = lazy(() => import("./components/pages/Stock/StockTransferOutIndex"));

//-----------Production-------------------
const ProductionIndex = lazy(() => import("./components/pages/Production/ProductionIndex"));

//-----------PaymentReceipts-------------------
const PaymentReceiptsIndex = lazy(() => import("./components/pages/PaymentReceipts/PaymentReceiptsIndex"));

//-----------JournalEntry-------------------
const JournalEntryIndex = lazy(() => import("./components/pages/JournalEntry/JournalEntryIndex"));

//-----------Reports-------------------
const LedgerReport = lazy(() => import("./components/pages/Reports/LedgerReport"));
const PurchaseReport = lazy(() => import("./components/pages/Reports/PurchaseReport"));
const PurchaseOrderStatusReport = lazy(() => import("./components/pages/Reports/PurchaseOrderStatusReport"));
const VendorandItemWisePurchaseReport = lazy(() => import("./components/pages/Reports/VendorandItemWisePurchaseReport"));
const VendorAgeingReport = lazy(() => import("./components/pages/Reports/VendorAgeingReport"));
const SaleReport = lazy(() => import("./components/pages/Reports/SaleReport"));
const CustomerandItemWiseSaleReport = lazy(() => import("./components/pages/Reports/CustomerandItemWiseSaleReport"));
const CustomerAgeingReport = lazy(() => import("./components/pages/Reports/CustomerAgeingReport"));
const SaleOrderStatusReport = lazy(() => import("./components/pages/Reports/SaleOrderStatusReport"));
const SaleGstReport = lazy(() => import("./components/pages/Reports/SaleGstReport"));
const StockTransferReport = lazy(() => import("./components/pages/Reports/StockTransferReport"));
const CashBook = lazy(() => import("./components/pages/Reports/CashBook"));
const ReportsHub = lazy(() => import("./components/pages/Reports/ReportsHub"));

//-----------PurchaseReports-------------------
const PurchaseOrderWiseReport = lazy(() => import("./components/pages/PurchaseReports/PurchaseOrderWiseReport"));
const PurchaseInvoiceRegister = lazy(() => import("./components/pages/PurchaseReports/PurchaseInvoiceRegister"));
const PurchaseSummaryBillWise = lazy(() => import("./components/pages/PurchaseReports/PurchaseSummaryBillWise"));
const PurchasePriceList = lazy(() => import("./components/pages/PurchaseReports/PurchasePriceList"));
const PurchasePaymentReminder = lazy(() => import("./components/pages/PurchaseReports/PurchasePaymentReminder"));
const PurchaseBillWiseOutstanding = lazy(() => import("./components/pages/PurchaseReports/PurchaseBillWiseOutstanding"));
const VendorBillWisePaymentDetails = lazy(() => import("./components/pages/PurchaseReports/VendorBillWisePaymentDetails"));
const VendorAgeingBillToBill = lazy(() => import("./components/pages/PurchaseReports/VendorAgeingBillToBill"));
const VendorAgeingFIFO = lazy(() => import("./components/pages/PurchaseReports/VendorAgeingFIFO"));
const VendorGroupOutstandingReport = lazy(() => import("./components/pages/PurchaseReports/VendorGroupOutstandingReport"));

//-----------SaleReports-------------------
const SalePriceList = lazy(() => import("./components/pages/SaleReports/SalePriceList"));
const SaleInvoiceRegister = lazy(() => import("./components/pages/SaleReports/SaleInvoiceRegister"));
const SaleSummaryBillWise = lazy(() => import("./components/pages/SaleReports/SaleSummaryBillWise"));
const SalesManCommissionSummary = lazy(() => import("./components/pages/SaleReports/SalesManCommissionSummary"));
const SalesManCommissionPending = lazy(() => import("./components/pages/SaleReports/SalesManCommissionPending"));
const SalesManCommissionExceeded = lazy(() => import("./components/pages/SaleReports/SalesManCommissionExceeded"));
const PaymentRemainder = lazy(() => import("./components/pages/SaleReports/PaymentRemainder"));
const BillWiseOutstanding = lazy(() => import("./components/pages/SaleReports/BillWiseOutstanding"));
const ClientBillWisePaymentDetails = lazy(() => import("./components/pages/SaleReports/ClientBillWisePaymentDetails"));
const ClientAgeingAnalysisBillWise = lazy(() => import("./components/pages/SaleReports/ClientAgeingAnalysisBillWise"));
const ClientAgeingFIFO = lazy(() => import("./components/pages/SaleReports/ClientAgeingFIFO"));
const ClientGroupOutstandingReport = lazy(() => import("./components/pages/SaleReports/ClientGroupOutstandingReport"));

//-----------StockReports-------------------
const StockMovementReport = lazy(() => import("./components/pages/StockReports/StockMovementReport"));
const StockSummaryReport = lazy(() => import("./components/pages/StockReports/StockSummaryReport"));
const StockAnalysisReport = lazy(() => import("./components/pages/StockReports/StockAnalysisReport"));
const StockAgeingReport = lazy(() => import("./components/pages/StockReports/StockAgeingReport"));
const RateAnalysisReport = lazy(() => import("./components/pages/StockReports/RateAnalysisReport"));
const ProductionWiseStockReport = lazy(() => import("./components/pages/StockReports/ProductionWiseStockReport"));

//-----------FinancialStatementsReports-------------------
const DelayedPaymentReport = lazy(() => import("./components/pages/FinancialStatementsReport/DelayedPaymentReport"));
const DelayedCollectionReport = lazy(() => import("./components/pages/FinancialStatementsReport/DelayedCollectionReport"));
const CollectionReport = lazy(() => import("./components/pages/FinancialStatementsReport/CollectionReport"));
const PaymentsReport = lazy(() => import("./components/pages/FinancialStatementsReport/PaymentsReport"));
const JournalBookReport = lazy(() => import("./components/pages/FinancialStatementsReport/JournalBookReport"));
const BankReceiptJournalReport = lazy(() => import("./components/pages/FinancialStatementsReport/BankReceiptJournalReport"));
const BankPaymentJournalReport = lazy(() => import("./components/pages/FinancialStatementsReport/BankPaymentJournalReport"));
const DayBookReport = lazy(() => import("./components/pages/FinancialStatementsReport/DayBookReport"));
const DayBookSummaryReport = lazy(() => import("./components/pages/FinancialStatementsReport/DayBookSummaryReport"));
const SaleJournalReport = lazy(() => import("./components/pages/FinancialStatementsReport/SaleJournalReport"));
const PurchaseJournalReport = lazy(() => import("./components/pages/FinancialStatementsReport/PurchaseJournalReport"));
const FinancialJournalHub = lazy(() => import("./components/pages/FinancialStatementsReport/FinancialJournalHub"));
const BankBookReport = lazy(() => import("./components/pages/FinancialStatementsReport/BankBookReport"));

//-----------MISReports-------------------
const RatioAnalysisReport = lazy(() => import("./components/pages/MISReports/RatioAnalysisReport"));
const CashFlowStatement = lazy(() => import("./components/pages/MISReports/CashFlowStatement"));
const ProfitLossMonthWise = lazy(() => import("./components/pages/MISReports/ProfitLossMonthWise"));
const FundFlowStatement = lazy(() => import("./components/pages/MISReports/FundFlowStatement"));
const BalanceSheet = lazy(() => import("./components/pages/MISReports/BalanceSheet"));
const BalanceSheetTView = lazy(() => import("./components/pages/MISReports/BalanceSheetTView"));
const TrialBalanceReport = lazy(() => import("./components/pages/MISReports/TrialBalanceReport"));
const TrialBalanceAdvancedReport = lazy(() => import("./components/pages/MISReports/TrialBalanceAdvancedReport"));
const IncomeExpenseReport = lazy(() => import("./components/pages/AccountsReports/IncomeExpenseReport"));
const ExpenseReport = lazy(() => import("./components/pages/AccountsReports/ExpenseReport"));
const DetailedInvoiceReport = lazy(() => import("./components/pages/AccountsReports/DetailedInvoiceReport"));

//-----------ItemProfitabilityReports-------------------
const StockBasedReport = lazy(() => import("./components/pages/ItemProfitabilityReports/StockBasedReport"));
const SalesBasedReport = lazy(() => import("./components/pages/ItemProfitabilityReports/SalesBasedReport"));
const BatchBasedReport = lazy(() => import("./components/pages/ItemProfitabilityReports/BatchBasedReport"));
const PartyWiseReport = lazy(() => import("./components/pages/ItemProfitabilityReports/PartyWiseReport"));
const SalesManWiseReport = lazy(() => import("./components/pages/ItemProfitabilityReports/SalesManWiseReport"));
const StockMinimalLevelReport = lazy(() => import("./components/pages/ItemProfitabilityReports/StockMinimalLevelReport"));
const ItemMovementAnalysisReport = lazy(() => import("./components/pages/ItemProfitabilityReports/ItemMovementAnalysisReport"));

//-----------InventoryReports-------------------
const StockDayBook = lazy(() => import("./components/pages/InventoryReports/StockDayBook"));
const StockLedger = lazy(() => import("./components/pages/InventoryReports/StockLedger"));
const DailyItemMovement = lazy(() => import("./components/pages/InventoryReports/DailyItemMovement"));
const MonthlyItemMovement = lazy(() => import("./components/pages/InventoryReports/MonthlyItemMovement"));
const FastSlowMovingItems = lazy(() => import("./components/pages/InventoryReports/FastSlowMovingItems"));
const StockTrail = lazy(() => import("./components/pages/InventoryReports/StockTrail"));
const StockTrailMonthWise = lazy(() => import("./components/pages/InventoryReports/StockTrailMonthWise"));
const StockValuation = lazy(() => import("./components/pages/InventoryReports/StockValuation"));
const StockTransferInventory = lazy(() => import("./components/pages/InventoryReports/StockTransferInventory"));

//-----------EInvoice-------------------
const EInvoiceIndex = lazy(() => import("./components/pages/EInvoice/EInvoiceIndex"));
const GSTItemBulkUpdate = lazy(() => import("./components/pages/GSTBulkUpdate/GSTItemBulkUpdate"));

//-----------SalesAnalysisReports-------------------
const SalesRegisterReport = lazy(() => import("./components/pages/SalesAnalysisReports/SalesRegisterReport"));
const SalesAnalysisReport = lazy(() => import("./components/pages/SalesAnalysisReports/SalesAnalysisReport"));
const DespatchAnalysisReport = lazy(() => import("./components/pages/SalesAnalysisReports/DespatchAnalysisReport"));
const SaleReturnAnalysisReport = lazy(() => import("./components/pages/SalesAnalysisReports/SaleReturnAnalysisReport"));
const YearOnYearSalesReport = lazy(() => import("./components/pages/SalesAnalysisReports/YearOnYearSalesReport"));

//-----------GSTReports-------------------
const TaxRegisterSale = lazy(() => import("./components/pages/GSTReports/TaxRegisterSale"));
const TaxRegisterPurchase = lazy(() => import("./components/pages/GSTReports/TaxRegisterPurchase"));
const HSNTaxSummary = lazy(() => import("./components/pages/GSTReports/HSNTaxSummary"));
const PurchaseHSNSummary = lazy(() => import("./components/pages/GSTReports/PurchaseHSNSummary"));
const HSNSummary = lazy(() => import("./components/pages/GSTReports/HSNSummary"));
const GSTR3BReport = lazy(() => import("./components/pages/GSTReports/GSTR3BReport"));
const GSTR3BFormatReport = lazy(() => import("./components/pages/GSTReports/GSTR3BFormatReport"));

//-----------PurchaseAnalysisReports-------------------
const PurchaseRegisterReport = lazy(() => import("./components/pages/PurchaseAnalysisReports/PurchaseRegisterReport"));
const OrderAnalysisReport = lazy(() => import("./components/pages/PurchaseAnalysisReports/OrderAnalysisReport"));
const PurchaseAnalysisReport = lazy(() => import("./components/pages/PurchaseAnalysisReports/PurchaseAnalysisReport"));
const PurchaseReturnAnalysisReport = lazy(() => import("./components/pages/PurchaseAnalysisReports/PurchaseReturnAnalysisReport"));
const YearOnYearPurchaseReport = lazy(() => import("./components/pages/PurchaseAnalysisReports/YearOnYearPurchaseReport"));

//-----------AssetReports-------------------
const AssetRegistration = lazy(() => import("./components/pages/AssetReports/AssetRegistration"));
const AssetDepreciation = lazy(() => import("./components/pages/AssetReports/AssetDepreciation"));

// ---------------- Error Boundary for Lazy Loading ---------------- //
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>Failed to load this page. Please try again.</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '10px 24px', background: '#1a1f3a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------- Theme ---------------- //
const theme = createTheme({
  palette: {
    primary: { main: "#1a1f3a" },
    secondary: { main: "#0f1419" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
        },
      },
    },
  },
});

// ---------------- Route-based AppBar color ---------------- //
// Auto-built from reportGroups — each group's color applies to all its report routes
const ROUTE_COLORS = (() => {
  const map = { '/ReportsHub': '#2c3e6e' };
  const groups = [
    { color: '#0d47a1', routes: ['/ProfitLossStatement','/ProfitLossStatementTView','/TradingProfitLoss','/ProfitLossMonthWise','/BalanceSheet','/BalanceSheetTView','/CashFlowStatement','/FundFlowStatement','/DepreciationStatement','/BankReconciliation','/TDSRegister'] },
    { color: '#263078', routes: ['/DelayedPaymentReport','/DelayedCollectionReport','/CollectionReport','/PaymentsReport','/JournalBookReport','/BankReceiptJournalReport','/BankPaymentJournalReport','/DayBookReport','/DayBookSummaryReport','/SaleJournalReport','/PurchaseJournalReport','/FinancialJournalHub','/RatioAnalysisReport','/BankBookReport'] },
    { color: '#4e342e', routes: ['/Ledger','/CashBook','/Outstanding','/ExpenseReport','/DetailedInvoice','/IncomeExpenseReport','/ExpenseAdvancedReport'] },
    { color: '#bf360c', routes: ['/PurchaseReport','/PurchaseOrderStatusReport','/VendorandItemWisePurchaseReport','/VendorAgeingReport','/PurchaseRegister','/OrderAnalysisReport','/PurchaseAnalysisReport','/PurchaseReturnAnalysisReport','/YearOnYearPurchaseReport'] },
    { color: '#1b5e20', routes: ['/SalesReport','/SalesOrderReport','/CustomerWiseSalesReport','/CustomerAgeingReport','/SalesRegister','/SalesAnalysis','/DespatchAnalysis','/SaleReturnAnalysis','/YearOnYearSales'] },
    { color: '#4a148c', routes: ['/StockTransferReport','/StockMovementReport','/StockSummaryReport','/StockAnalysisReport','/StockAgeingReport','/RateAnalysisReport','/ProductionWiseStockReport'] },
    { color: '#00695c', routes: ['/StockDayBook','/StockLedger','/DailyItemMovement','/MonthlyItemMovement','/StockTrail','/StockTrailTransactionWise','/StockTrailMonthWise','/StockValuation','/StockTransfer','/FastSlowMovingItems','/StockTrailTransactionWise'] },
    { color: '#1c2266', routes: ['/GSTR1','/GSTR3B','/GSTR3BReport','/GSTR3BFormatReport','/SaleGstReport','/TaxRegisterSale','/TaxRegisterPurchase','/HSNTaxSummary','/PurchaseHSNSummary','/HSNSummary'] },
    { color: '#10586a', routes: ['/StockBasedReport', '/SalesBasedReport', '/BatchBasedReport', '/PartyWiseReport', '/SalesManWiseReport', '/StockMinimalLevelReport', '/ItemMovementAnalysisReport'] }, // Item Profitability
    { color: '#1c2266', routes: ['/SaleInvoiceRegister','/SaleSummaryBillWise','/SalesManCommissionSummary','/SalesManCommissionPending','/SalesManCommissionExceeded','/SalePriceList','/PaymentRemainder','/BillWiseOutstanding','/ClientGroupOutstandingReport','/ClientBillWisePaymentDetails','/ClientAgeingAnalysisBillWise','/ClientAgeingFIFO'] },
    { color: '#0d8392', routes: ['/PurchaseOrderWiseReport','/PurchaseInvoiceRegister','/PurchaseSummaryBillWise','/PurchasePriceList','/PurchasePaymentReminder','/PurchaseBillWiseOutstanding','/VendorGroupOutstandingReport','/VendorBillWisePaymentDetails','/VendorAgeingBillToBill','/VendorAgeingFIFO'] },
    { color: '#0b9f69', routes: ['/AssetRegistration', '/AssetDepreciation'] }, // Assets
  ];
  groups.forEach(({ color, routes }) => routes.forEach(r => { if (!map[r]) map[r] = color; }));
  return map;
})();

const useAppBarColor = () => {
  const location = useLocation();
  const { appBarColor } = useCustomTheme();
  return ROUTE_COLORS[location.pathname] || appBarColor;
};

// ---------------- Layout ---------------- //
const AppBarWithColor = ({ companyName, username, isMobile }) => {
  const appBarColor = useAppBarColor();
  const history = useHistory();
  const { totalUnread } = useChat();
  return (
    <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, backgroundColor: appBarColor, transition: 'background-color 0.3s ease' }}>
      <Toolbar sx={{ minHeight: '48px !important', height: '48px', px: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase", mr: 2 }}>
          <Link to="/Home" style={{ textDecoration: "none", color: "inherit" }}>
            <span style={{ fontFamily: 'Verdana, sans-serif', fontSize: '14px' }}>{companyName}</span>
          </Link>
        </Typography>
        {!isMobile && username !== "Counter" && <HorizontalNavbar />}
        <Box sx={{ flexGrow: 1 }} />
        {/* Chat shortcut — always visible in AppBar */}
        <Tooltip title={totalUnread > 0 ? `${totalUnread} unread message${totalUnread > 1 ? 's' : ''}` : 'Chat'} arrow>
          <IconButton onClick={() => history.push('/InternalMessaging')} sx={{ color: 'white', mr: 0.5 }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: 20 }}>💬</span>
              {totalUnread > 0 && (
                <Box sx={{
                  position: 'absolute', top: -6, right: -8,
                  background: '#ef4444', color: '#fff',
                  borderRadius: '50%', minWidth: 16, height: 16,
                  fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 3px', lineHeight: 1,
                }}>
                  {totalUnread > 99 ? '99+' : totalUnread}
                </Box>
              )}
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip title="Theme Settings" arrow>
          <IconButton onClick={() => history.push('/ThemeSettings')} sx={{ color: 'white', mr: 0.5 }}>
            <Palette sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Logout />
      </Toolbar>
    </AppBar>
  );
};

const Layout = () => {
  const { companyName, roleName, username, isAuthenticated } = useAuth();
  const isMobile = useMediaQuery('(max-width:768px)');
  const { isDark } = useCustomTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
        {/* Session Timeout Component - only render when authenticated */}
        {isAuthenticated && <SessionTimeout />}

        {/* PWA Status Component */}
        <PWAStatus
          showInstallPrompt={true}
          showUpdatePrompt={true}
          showOfflineStatus={true}
        />

        {/* Header with integrated menu */}
        <AppBarWithColor companyName={companyName} username={username} isMobile={isMobile} />

        {/* Content */}
        <Box
          className="app-content-area"
          sx={{
            pt: '48px',
            pb: isMobile ? '70px' : 0,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: isDark ? '#050d1a' : '#f0f4f8',
            transition: 'background-color 0.3s ease',
          }}
        >
          <Box className="app-route-content" sx={{ p: 0, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <ErrorBoundary>
            <Suspense fallback={null}>
            <Switch>
              <Route path="/" exact render={() => {
                const role = (roleName || '').toLowerCase();
                const isExecutive = role.includes('executive') || role.includes('salesman') || role.includes('sales man');
                return isExecutive ? <Redirect to="/SalesManDashboard" /> : <Home />;
              }} />
              <Route path="/Home" exact render={() => {
                const role = (roleName || '').toLowerCase();
                const isExecutive = role.includes('executive') || role.includes('salesman') || role.includes('sales man');
                return isExecutive ? <Redirect to="/SalesManDashboard" /> : <Home />;
              }} />

              {/* Role */}
              <Route path="/RoleIndex" component={RoleIndex} />
              <Route path="/RoleDetail/:id" component={RoleDetail} />

              {/* User */}
              <Route path="/UserIndex" component={UserIndex} />
              <Route path="/UserCreate" component={UserCreate} />
              <Route path="/UserEdit/:id" component={UserEdit} />
              <Route path="/UserDetail/:id" component={UserDetail} />
              <Route path="/PasswordChange" component={PasswordChange} />
              <Route path="/ThemeSettings" component={ThemeSettings} />

              {/* Client */}
              <Route path="/ClientIndex" component={ClientIndex} />

              {/* Vendor */}
              <Route path="/VendorIndex" component={VendorIndex} />

              {/* Product */}
              <Route path="/ProductCategoryIndex" component={ProductCategoryIndex} />
              <Route path="/ProductItemIndex" component={ProductItemIndex} />
              <Route path="/ProductBrandIndex" component={ProductBrandIndex} />
              {/* <Route path="/ProductRelationIndex" component={ProductRelationIndex} /> */}

              {/* Bank */}
              <Route path="/BankIndex" component={BankIndex} />

              {/* Master */}
              <Route path="/MasterDashboard" component={MasterDashboard} />
              <Route path="/ClientIndex" component={ClientIndex} />
              <Route path="/VendorIndex" component={VendorIndex} />

              {/* AccountHead */}
              <Route path="/AccountHeadDashboard" component={AccountHeadDashboard} />
              <Route path="/AccountHeadIndex" component={AccountHeadIndex} />
              <Route path="/AssetIndex" component={AssetIndex} />
              <Route path="/CapitalIndex" component={CapitalIndex} />
              <Route path="/OtherOptionIndex" component={OtherOptionIndex} />
              <Route path="/SalesExecutiveIndex" component={SalesExecutiveIndex} />
              <Route path="/SalesManDashboard" component={SalesManDashboard} />
              <Route path="/SalesManSaleOrderIndex" component={SalesManSaleOrderIndex} />
              <Route path="/BalanceSheetHeadIndex" component={BalanceSheetHeadIndex} />

              {/* PurchaseOrder */}
              <Route path="/PurchaseOrderIndex" component={PurchaseOrderIndex} />

              {/* Purchase */}
              <Route path="/PurchaseIndex" component={PurchaseIndex} />

              {/* PurchaseReturn */}
              <Route path="/PurchaseReturnAgainstIndex" component={PurchaseReturnAgainstIndex} />

              {/* Quotation */}
              <Route path="/QuotationIndex" component={QuotationIndex} />

              {/* SalesOrder */}
              <Route path="/SaleOrderIndex" component={SalesOrderIndex} />

              {/* Sale */}
              <Route path="/SaleIndex" component={SaleIndex} />

              {/* SaleReturn */}
              <Route path="/SalesReturnIndex" component={SaleReturnIndex} />

              {/* Stock */}
              <Route path="/StockTransferInIndex" component={StockTransferInIndex} />
              <Route path="/StockTransferOutIndex" component={StockTransferOutIndex} />

              {/* Production */}
              <Route path="/ProductionIndex" component={ProductionIndex} />

              {/* PaymentReceipts */}
              <Route path="/PaymentAndReceiptIndex" component={PaymentReceiptsIndex} />

              {/* JournalEntry */}
              <Route path="/JournalEntryIndex" component={JournalEntryIndex} />

              {/* Reports */}
              <Route path="/ReportsHub" component={ReportsHub} />
              <Route path="/Ledger" component={LedgerReport} />
              <Route path="/PurchaseReport" component={PurchaseReport} />
              <Route path="/PurchaseOrderStatusReport" component={PurchaseOrderStatusReport} />
              <Route path="/VendorAgeingReport" component={VendorAgeingReport} />
              <Route path="/VendorandItemWisePurchaseReport" component={VendorandItemWisePurchaseReport} />
              <Route path="/PurchaseOrderWiseReport" component={PurchaseOrderWiseReport} />
              <Route path="/PurchaseInvoiceRegister" component={PurchaseInvoiceRegister} />
              <Route path="/PurchaseSummaryBillWise" component={PurchaseSummaryBillWise} />
              <Route path="/PurchasePriceList" component={PurchasePriceList} />
              <Route path="/PurchasePaymentReminder" component={PurchasePaymentReminder} />
              <Route path="/PurchaseBillWiseOutstanding" component={PurchaseBillWiseOutstanding} />
              <Route path="/VendorBillWisePaymentDetails" component={VendorBillWisePaymentDetails} />
              <Route path="/VendorAgeingBillToBill" component={VendorAgeingBillToBill} />
              <Route path="/VendorAgeingFIFO" component={VendorAgeingFIFO} />
              <Route path="/VendorGroupOutstandingReport" component={VendorGroupOutstandingReport} />
              <Route path="/SalePriceList" component={SalePriceList} />
              <Route path="/SaleInvoiceRegister" component={SaleInvoiceRegister} />
              <Route path="/SaleSummaryBillWise" component={SaleSummaryBillWise} />
              <Route path="/SalesManCommissionSummary" component={SalesManCommissionSummary} />
              <Route path="/SalesManCommissionPending" component={SalesManCommissionPending} />
              <Route path="/SalesManCommissionExceeded" component={SalesManCommissionExceeded} />
              <Route path="/PaymentRemainder" component={PaymentRemainder} />
              <Route path="/BillWiseOutstanding" component={BillWiseOutstanding} />
              <Route path="/ClientBillWisePaymentDetails" component={ClientBillWisePaymentDetails} />
              <Route path="/ClientAgeingAnalysisBillWise" component={ClientAgeingAnalysisBillWise} />
              <Route path="/ClientAgeingFIFO" component={ClientAgeingFIFO} />
              <Route path="/ClientGroupOutstandingReport" component={ClientGroupOutstandingReport} />
              <Route path="/StockMovementReport" component={StockMovementReport} />
              <Route path="/StockSummaryReport" component={StockSummaryReport} />
              <Route path="/StockAnalysisReport" component={StockAnalysisReport} />
              <Route path="/StockAgeingReport" component={StockAgeingReport} />
              <Route path="/RateAnalysisReport" component={RateAnalysisReport} />
              <Route path="/ProductionWiseStockReport" component={ProductionWiseStockReport} />
              <Route path="/StockBasedReport" component={StockBasedReport} />
              <Route path="/SalesBasedReport" component={SalesBasedReport} />
              <Route path="/BatchBasedReport" component={BatchBasedReport} />
              <Route path="/PartyWiseReport" component={PartyWiseReport} />
              <Route path="/SalesManWiseReport" component={SalesManWiseReport} />
              <Route path="/StockMinimalLevelReport" component={StockMinimalLevelReport} />
              <Route path="/ItemMovementAnalysisReport" component={ItemMovementAnalysisReport} />
              <Route path="/StockDayBook" component={StockDayBook} />
              <Route path="/StockLedger" component={StockLedger} />
              <Route path="/DailyItemMovement" component={DailyItemMovement} />
              <Route path="/MonthlyItemMovement" component={MonthlyItemMovement} />
              <Route path="/FastSlowMovingItems" component={FastSlowMovingItems} />
              <Route path="/StockTrail" component={StockTrail} />
              <Route path="/StockTrailTransactionWise" component={StockTrail} />
              <Route path="/StockTrailMonthWise" component={StockTrailMonthWise} />
              <Route path="/StockValuation" component={StockValuation} />
              <Route path="/StockTransfer" component={StockTransferInventory} />
              <Route path="/SalesReport" component={SaleReport} />
              <Route path="/CustomerWiseSalesReport" component={CustomerandItemWiseSaleReport} />
              <Route path="/CustomerAgeingReport" component={CustomerAgeingReport} />
              <Route path="/SalesOrderReport" component={SaleOrderStatusReport} />
              <Route path="/SalesRegister" component={SalesRegisterReport} />
              <Route path="/SalesAnalysis" component={SalesAnalysisReport} />
              <Route path="/DespatchAnalysis" component={DespatchAnalysisReport} />
              <Route path="/SaleReturnAnalysis" component={SaleReturnAnalysisReport} />
              <Route path="/YearOnYearSales" component={YearOnYearSalesReport} />
              <Route path="/PurchaseRegister" component={PurchaseRegisterReport} />
              <Route path="/OrderAnalysisReport" component={OrderAnalysisReport} />
              <Route path="/PurchaseAnalysisReport" component={PurchaseAnalysisReport} />
              <Route path="/PurchaseReturnAnalysisReport" component={PurchaseReturnAnalysisReport} />
              <Route path="/YearOnYearPurchaseReport" component={YearOnYearPurchaseReport} />
              <Route path="/CashBook" component={CashBook} />
              <Route path="/SaleGstReport" component={SaleGstReport} />
              <Route path="/StockTransferReport" component={StockTransferReport} />
              <Route path="/TrialBalanceReport" component={TrialBalanceReport} />
               <Route path="/TrialBalanceAdvancedReport" component={TrialBalanceAdvancedReport} />
              <Route path="/DelayedPaymentReport" component={DelayedPaymentReport} />
              <Route path="/DelayedCollectionReport" component={DelayedCollectionReport} />
              <Route path="/CollectionReport" component={CollectionReport} />
              <Route path="/PaymentsReport" component={PaymentsReport} />
              <Route path="/JournalBookReport" component={JournalBookReport} />
              <Route path="/BankReceiptJournalReport" component={BankReceiptJournalReport} />
              <Route path="/BankPaymentJournalReport" component={BankPaymentJournalReport} />
              <Route path="/BankBookReport" component={BankBookReport} />
              <Route path="/DayBookReport" component={DayBookReport} />
              <Route path="/DayBookSummaryReport" component={DayBookSummaryReport} />
              <Route path="/SaleJournalReport" component={SaleJournalReport} />
              <Route path="/PurchaseJournalReport" component={PurchaseJournalReport} />
              <Route path="/RatioAnalysisReport" component={RatioAnalysisReport} />
              <Route path="/CashFlowStatement" component={CashFlowStatement} />
              <Route path="/ProfitLossMonthWise" component={ProfitLossMonthWise} />
              <Route path="/FundFlowStatement" component={FundFlowStatement} />
              <Route path="/BalanceSheet" component={BalanceSheet} />
              <Route path="/BalanceSheetTView" component={BalanceSheetTView} />
              <Route path="/IncomeExpenseReport" component={IncomeExpenseReport} />
              <Route path="/ExpenseAdvancedReport" component={ExpenseReport} />
              <Route path="/DetailedInvoice" component={DetailedInvoiceReport} />
              <Route path="/TaxRegisterSale" component={TaxRegisterSale} />
              <Route path="/GSTR3BReport" component={GSTR3BReport} />
              <Route path="/GSTR3BFormatReport" component={GSTR3BFormatReport} />
              <Route path="/TaxRegisterPurchase" component={TaxRegisterPurchase} />
              <Route path="/HSNTaxSummary" component={HSNTaxSummary} />
              <Route path="/PurchaseHSNSummary" component={PurchaseHSNSummary} />
              <Route path="/HSNSummary" component={HSNSummary} />

              {/* EInvoice */}
              <Route path="/EInvoiceIndex" component={EInvoiceIndex} />
              <Route path="/GSTItemBulkUpdate" component={GSTItemBulkUpdate} />

              {/* Asset Reports */}
              <Route path="/AssetRegistration" component={AssetRegistration} />
              <Route path="/AssetDepreciation" component={AssetDepreciation} />

              {/* Messaging */}
              <Route path="/InternalMessaging" component={InternalMessaging} />

              {/* Company */}
              <Route path="/CompanyEdit" component={CompanyEdit} />

            </Switch>
            </Suspense>
            </ErrorBoundary>
          </Box>
        </Box>

        {/* Mobile Bottom Navigation */}
        {isMobile && username !== "Counter" && <MobileBottomNav />}
    </ThemeProvider>
  );
};

// ---------------- Root ---------------- //
const SESSION_KEY = 'app_current_path';

// Create history with the saved path as the starting location,
// so the Router's initial location is correct before any component mounts.
const savedPath = sessionStorage.getItem(SESSION_KEY);
const appHistory = createBrowserHistory();
if (savedPath && savedPath !== '/') {
  appHistory.replace(savedPath);
  sessionStorage.removeItem(SESSION_KEY);
}

const App = () => {
  useEffect(() => {
    // ── URL Masking: intercept pushState/replaceState to save real path ──
    const APP_DISPLAY = window.location.origin;

    const _push    = window.history.pushState.bind(window.history);
    const _replace = window.history.replaceState.bind(window.history);

    window.history.pushState = (s, t, u) => {
      if (u && u !== APP_DISPLAY) sessionStorage.setItem(SESSION_KEY, u);
      _push(s, t, APP_DISPLAY);
    };
    window.history.replaceState = (s, t, u) => {
      if (u && u !== APP_DISPLAY) sessionStorage.setItem(SESSION_KEY, u);
      _replace(s, t, APP_DISPLAY);
    };

    // Mask the URL now (after history is set up)
    window.history.replaceState(null, '', APP_DISPLAY);

    return () => {
      window.history.pushState    = _push;
      window.history.replaceState = _replace;
    };
  }, []);

  useEffect(() => {
    // Register PWA service worker via vite-plugin-pwa
    const updateSW = registerSW({
      onNeedRefresh() {
        console.log('SW update available');
        // The PWAStatus component handles the update prompt UI
        if (window.confirm('A new version is available. Reload to update?')) {
          updateSW(true);
        }
      },
      onOfflineReady() {
        console.log('App is ready for offline use');
      },
    });

    // Handle PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      window.deferredPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  return (
    <Router history={appHistory}>
      <AuthProvider>
        <CustomThemeProvider>
          <ChatProvider>
            <Layout />
          </ChatProvider>
        </CustomThemeProvider>
      </AuthProvider>
    </Router>
  );
};
export default App;
